"use client";
//import react
import React, { useEffect, useState, useMemo } from "react";
import Image from "next/image";
// External Lib
import { Box } from "@mui/material";
import { format } from "date-fns";
// Internal Lib
import {
  Input,
  Dropdown,
  Label,
  CustomDatePicker,
  MultipleSelect,
  DropdownAutoSuggest
} from "@int-traderjoes/ui-components-library";
// Types
import { IVendorAssessmentFormData } from "@/context/data.types";
// constants
import Constants from "@/static/constants/constants.json";
// Utils
import { UpArrow, DownArrow } from "@/assets/images/CommonSvgUtils";
import { formatRawDropdownOptions, toNumberIfPossible, decodeHtmlEntities, sanitizeVendorCorporateName, formatValuesForMultipleSelect } from "@/utils/helper";
import {
  formatCountryOfOriginForSave,
  parseCountryOfOriginIds,
  isMetalDetectionXrayOtherSelected,
  resolveVendorAssessmentProductSpecificationType,
  shouldShowVendorAssessmentField,
} from "./utils/helper";
import { isVendorFieldsLockedForExistingVendor } from "@/utils/vendor/existingVendorFieldsLock";
import { isProduceRacVendorAssessmentLayout } from "@/utils/productSpecificationTypeUtils";

// Contexts
import { useFormContext } from "@/context/FormContext";
import { useTheme } from "@/context";
import { useLanguageContext } from "@/context/LanguageContext";

// Services
import AuthService from "@/services/authService";

//Custom Components
import FormHeader from "@/utils/FormHeader";
import CustomTooltip from "@/components/Tooltip";

//Assets
import helpIcon from "@/assets/images/ideation-phase-images/HelpIcon.svg";
import { DropdownData, DropdownItem } from "@/types/vendor.types";

interface VendorAssessmentFormProps {
  vendorAssessmentFormData: IVendorAssessmentFormData;
  vendorKey?: number;
  isVendor?: boolean;
  dropdownData: DropdownData;
  setDropdownData: React.Dispatch<React.SetStateAction<DropdownData>>;
  isExpanded: boolean;
  onToggle: () => void;
  formUpdated: () => void;
  isFormDisabled?: boolean;
  formHeaderText?: string;
  hasExistingVendor?: boolean | null;
  productSpecificationType?: string | null;
  disableProductName?: boolean;
}

type MultiSelectOption = {
  label: React.ReactNode;
  value: string | number;
};

// Dropdown expects string labels; keep these strictly as strings.
const YES_NO_OPTIONS = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];

const VendorAssessmentForm: React.FC<VendorAssessmentFormProps> = ({
  vendorAssessmentFormData,
  vendorKey,
  isVendor,
  dropdownData,
  setDropdownData,
  isExpanded,
  onToggle,
  formUpdated,
  isFormDisabled,
  formHeaderText,
  hasExistingVendor,
  productSpecificationType,
  disableProductName,
}) => {
  const { darkMode } = useTheme();
  const darkModeClass = darkMode ? "dark" : "light";
  const displayMode = () => { if (darkMode) { return "dark" } else { return "light" } }
  const {
    vendorAssessmentForm,
    setVendorAssessmentForm,
    vendorAssessmentFormErrors,
    setVendorAssessmentFormErrors,
    isVendorAssessmentSubmitClicked,
    vendorInformationForms,
    setVendorInformationForms,
    setVendorContactInformationForm,
    setIngredientDetailsForm,
  } = useFormContext();
  const [optionsSelected, setOptionsSelected] = useState<MultiSelectOption[]>([]);
  const [countryOptionsSelected, setCountryOptionsSelected] = useState<MultiSelectOption[]>([]);

  const { languageText } = useLanguageContext();
  const vendorAssessmentLabels = languageText?.LABELS?.VENDOR_INFORMATION?.VENDOR_ASSESSMENT_FORM;
  const infoIconMessages = languageText?.MESSAGES?.INFO_ICON_MESSAGES?.VENDOR_INFORMATION?.VENDOR_ASSESSMENT_FORM;
  const formHeader = formHeaderText ?? languageText?.TABLE_AND_FORM_HEADER?.FORM_HEADER?.VENDOR_ASSESSMENT_FORM;
  const placeholderTexts = languageText?.PLACEHOLDERS?.COMMON;

  // Fetching field permissions
  let vendorAssessmentFormPermissions: any = {};
  vendorAssessmentFormPermissions = AuthService.fetchPermissions("VendorAssessment");

  const effectiveProductSpecificationType = useMemo(
    () =>
      resolveVendorAssessmentProductSpecificationType(
        productSpecificationType
      ),
    [productSpecificationType]
  );

  const isProduceRacLayout = isProduceRacVendorAssessmentLayout(
    effectiveProductSpecificationType
  );

  const showVaField = (field: keyof IVendorAssessmentFormData): boolean =>
    shouldShowVendorAssessmentField(
      field,
      effectiveProductSpecificationType,
      vendorAssessmentFormPermissions[field]
    );

  const isFieldDisabled = (fieldName: string) => {
    if (isFormDisabled) return true;
    return false;
  };

  const isExistingVendorFieldsLocked = isVendorFieldsLockedForExistingVendor(
    hasExistingVendor
  );

  const manufactureSameSimilarAnswer =
    vendorAssessmentForm.currentlyManufactureSameSimilarProduct
      ?.toLowerCase()
      .trim() ?? "";

  // Input Change Handler
  const handleInputChange =
    (field: keyof typeof vendorAssessmentForm) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        let newValue = e.target.value;

        // Sanitize vendorName and productName to prevent characters that could break base64 encoding/decoding
        if (field === "vendorName" || field === "productName") {
          newValue = sanitizeVendorCorporateName(newValue);
        }

        setVendorAssessmentForm((prevData) => ({
          ...prevData,
          [field]: newValue,
        }));
        if (field === "vendorName") {
          vendorKey = vendorKey ?? 0;
          setVendorInformationForms((prevData) =>
            prevData.map((vendorForm, index) =>
              index === vendorKey
                ? {
                  ...vendorForm,
                  vendorCorporateName: newValue,
                }
                : vendorForm
            )
          );
          setIngredientDetailsForm((prevData) => ({
            ...prevData,
            [field]: newValue,
          }));
          setVendorContactInformationForm((prevData) => ({
            ...prevData,
            [field]: newValue,
          }));
        }
        formUpdated();
      };

  // Dropdown Change Handler
  const handleDropdownChange =
    (field: keyof typeof vendorAssessmentForm) =>
      (_: React.ChangeEvent<HTMLInputElement>, option: any) => {
        setVendorAssessmentForm((prevData) => {
          const isCleared =
            option == null || option.value === "" || option.value == null;

          // X clear — keep prior conditional side-effects untouched on select
          if (isCleared) {
            return {
              ...prevData,
              [field]: null,
            };
          }

          const updatedForm = {
            ...prevData,
            [field]: option.value,
          };

          // Ensure countryOfOrigin stores the ID (not the name)
          if (field === "countryOfOrigin") {
            const idValue = option?.id ?? option?.value;
            updatedForm["countryOfOrigin"] = isNaN(Number(idValue))
              ? idValue
              : Number(idValue);
          }

          // Handle conditional logic for specific fields
          if (field === "recallWithinLast5Years") {
            if (option.value.toLowerCase() === "no") {
              updatedForm["recallDetail"] = "Not Applicable";
            } else if (prevData["recallDetail"] === "Not Applicable") {
              updatedForm["recallDetail"] = "";
            }
          }

          if (field === "facilityFdaUsdaInspected") {
            if (option.label === "FDA") {
              updatedForm["usdaPlantNo"] = "Not Applicable";
            } else if (prevData["usdaPlantNo"] === "Not Applicable") {
              updatedForm["usdaPlantNo"] = "";
            }
          }

          if (field === "outsourceProduction") {
            if (option.value.toLowerCase() === "no") {
              updatedForm["outsourceDescripton"] = "Not Applicable";
            } else if (prevData["outsourceDescripton"] === "Not Applicable") {
              updatedForm["outsourceDescripton"] = "";
            }
          }

          if (field === "currentlyManufactureSameSimilarProduct") {
            if (option.value.toLowerCase() === "yes") {
              updatedForm["experienceWithSimilarType"] = "";
            } else if (option.value.toLowerCase() === "no") {
              updatedForm["mostSimilarProduct"] = "";
            } else {
              updatedForm["mostSimilarProduct"] = "";
              updatedForm["experienceWithSimilarType"] = "";
            }
          }

          if (field === "metalDetectionXrayOnProductLine") {
            if (!isMetalDetectionXrayOtherSelected(option.value)) {
              updatedForm["otherMetalDetectionXrayOnProductLine"] = "";
            }
          }

          return updatedForm;
        });
        formUpdated()
      };

    const handleMultipleSelection = (selected: MultiSelectOption[]) => {

    // Update the selected options state
    setOptionsSelected(selected);

    const ids = selected.map((option) => option.value).join(",");
    
    // Update form data with the new labels and values

    setVendorAssessmentForm((prevData) => ({
          ...prevData,
          // edi : labels,
          edi : ids,
        }));
  };

  const countryDropdownOptions = useMemo(
    () =>
      formatRawDropdownOptions(dropdownData.countries, "name", "id").map(
        (option: MultiSelectOption) => ({
          label: decodeHtmlEntities(String(option.label)),
          value: String(option.value),
        })
      ),
    [dropdownData.countries]
  );

  const metalDetectionXrayOptions = useMemo(
    () =>
      formatRawDropdownOptions(
        dropdownData.metalDetectionXrayOnProductLine ?? [],
        "name",
        "id"
      ),
    [dropdownData.metalDetectionXrayOnProductLine]
  );

  const showOtherMetalDetectionXrayField =
    showVaField("metalDetectionXrayOnProductLine") &&
    isMetalDetectionXrayOtherSelected(
      vendorAssessmentForm.metalDetectionXrayOnProductLine
    );

  const handleCountryMultipleSelection = (selected: MultiSelectOption[]) => {
    setCountryOptionsSelected(selected);
    const ids = selected.map((option) => String(option.value));
    setVendorAssessmentForm((prevData) => ({
      ...prevData,
      countryOfOrigin: formatCountryOfOriginForSave(ids),
    }));
    formUpdated();
  };

  // Date Change Handler
  const handleDateChange =
    (field: keyof typeof vendorAssessmentForm) => (date: Date | null) => {

      const formattedDate = date ? format(date, 'yyyy-MM-dd') : '';  // Format as 'yyyy-MM-dd'      
      setVendorAssessmentForm((prevData) => ({
        ...prevData,
        [field]: formattedDate,
      }));
      formUpdated()
    };

  const handleFieldFocus = (field: keyof IVendorAssessmentFormData): void => {
    setVendorAssessmentFormErrors((prevErrors) =>
      prevErrors.filter((errorField) => errorField !== field)
    );
  };

  const handleBlur = (field: keyof IVendorAssessmentFormData) => {
    const isOtherMetalDetectionField =
      field === "otherMetalDetectionXrayOnProductLine";
    const shouldValidateOtherMetalDetectionField =
      isOtherMetalDetectionField &&
      isMetalDetectionXrayOtherSelected(
        vendorAssessmentForm.metalDetectionXrayOnProductLine
      );

    if (
      isVendorAssessmentSubmitClicked &&
      (shouldValidateOtherMetalDetectionField ||
        (vendorAssessmentFormPermissions[field]?.isMandatory && showVaField(field)))
    ) {
      const fieldValue = vendorAssessmentForm[field];
      let isEmpty = false;
      
      // Special handling for EDI field which can be array or string
      if (field === "edi") {
        if (Array.isArray(fieldValue)) {
          isEmpty = fieldValue.length === 0;
        } else if (typeof fieldValue === "string") {
          isEmpty = fieldValue.trim() === "";
        } else {
          isEmpty = !fieldValue;
        }
      } else if (field === "countryOfOrigin") {
        isEmpty =
          parseCountryOfOriginIds(fieldValue).length === 0;
      } else {
        isEmpty = !fieldValue;
      }

      if (isEmpty) {
        setVendorAssessmentFormErrors((prev) => [...prev, field]);
      }
    }
  };

  useEffect(() => {
    const onloadEdiList = dropdownData?.edi ?? [];
    const selectedEdiIds = vendorAssessmentForm?.edi ?? [];
    // console.log("vendorAssessmentFormData", vendorAssessmentForm);
    // console.log("onloadEdiList", onloadEdiList, selectedEdiIds);
    // EDI change logic
    if (
      Array.isArray(onloadEdiList) &&
      Array.isArray(selectedEdiIds) &&
      onloadEdiList.length > 0 &&
      selectedEdiIds.length > 0
    ) {
      const formattedEdi = onloadEdiList
        .filter((edi: any) =>
          edi?.id && selectedEdiIds.some((selected: any) => selected.id?.toString() === edi.id?.toString())
        )
        .map((edi: any) => ({
          value: edi.id,
          label: decodeHtmlEntities(edi.name), // Decode HTML entities in EDI name
        }));

        // console.log("formattedEdi", formattedEdi);
      setOptionsSelected(formattedEdi);
    }

  }, [vendorAssessmentForm.edi, dropdownData.edi, setDropdownData]);

  useEffect(() => {
    if (!isProduceRacLayout || countryDropdownOptions.length === 0) {
      return;
    }

    const selectedIds = parseCountryOfOriginIds(
      vendorAssessmentForm?.countryOfOrigin
    );
    if (selectedIds.length === 0) {
      setCountryOptionsSelected([]);
      return;
    }

    setCountryOptionsSelected(
      formatValuesForMultipleSelect(selectedIds, countryDropdownOptions)
    );
  }, [
    vendorAssessmentForm.countryOfOrigin,
    countryDropdownOptions,
    isProduceRacLayout,
  ]);

  return (
    <Box
      className={`form-container ${isExpanded ? "form-container-expanded" : ""
        }`} 
    >
      <Box
        className={`form-header cursor-pointer ${isExpanded ? "form-header-expanded" : ""
          }`}
        onClick={onToggle}
      >
        <FormHeader title={formHeader} isExpanded={isExpanded} />
        <Box
          className="pr-4 cursor-pointer"
        >
          {isExpanded
            ? UpArrow("dark")
            : DownArrow(displayMode())}
        </Box>
      </Box>
      <form onSubmit={(e) => e.preventDefault()}>
        {isExpanded && (
          <Box className="field-group-two-fields vendor-assessment-form-container">

            {/* Vendor Name */}
            <Box hidden={!showVaField("vendorName")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("vendorName")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.VENDOR_NAME}
                htmlFor="vendorName"
                required={
                  vendorAssessmentFormPermissions.vendorName.isMandatory
                }
              />
              <Input
                id="vendorName"
                name="vendorName"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("vendorName")
                  ? "field-error-color"
                  : ""
                  } `}
                value={vendorAssessmentForm.vendorName}
                onChange={handleInputChange("vendorName")}
                disabled={
                  !vendorAssessmentFormPermissions.vendorName?.isEditable ||
                  isFieldDisabled("vendorName") ||
                  isExistingVendorFieldsLocked
                }
                onFocus={() => handleFieldFocus("vendorName")}
                onBlur={() => handleBlur("vendorName")}
                maxLength={Constants.VENDOR_CORPORATE_OR_VENDOR_NAME_MAX_LENGTH}
              />
            </Box>

            {/* Product Developer */}
            <Box hidden={!showVaField("productDeveloper")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("productDeveloper")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.PRODUCT_DEVELOPER}
                htmlFor="productDeveloper"
                required={
                  vendorAssessmentFormPermissions.productDeveloper.isMandatory
                }
              />
              <Dropdown
                id="productDeveloper"
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("productDeveloper")
                  ? "field-error-color"
                  : ""
                  } `}
                  
                // options={formatDropdownOptions(
                //   dropdownData.productDevelopers,
                //   "name",
                //   "id"
                // )}
                options={[{label:vendorAssessmentForm.productDeveloper,value:vendorAssessmentForm.productDeveloper}]}
                name="productDeveloper"

                // selectedValue={
                // isVendor && dropdownData?.productDevelopers?.length
                //   ? dropdownData.productDevelopers[0].id
                //   : Number(vendorAssessmentForm.productDeveloper)
                // }
                selectedValue={vendorAssessmentForm.productDeveloper}
 
                // dropdownData.productDevelopers.find(
                //   (developer) => developer.name === vendorAssessmentForm.productDeveloper
                // )?.id
                onChange={handleDropdownChange("productDeveloper")}
                disabled={!vendorAssessmentFormPermissions.productDeveloper?.isEditable || isVendor || isFieldDisabled("productDeveloper")}
                onFocus={() => handleFieldFocus("productDeveloper")}
                onBlur={() => handleBlur("productDeveloper")}
                    isClearable
                  />
            </Box>

            {/* Product Name */}
            <Box hidden={!showVaField("productName")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("productName")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.PRODUCT_NAME}
                htmlFor="productName"
                required={
                  vendorAssessmentFormPermissions.productName.isMandatory
                }
              />
              <Input
                id="productName"
                name="productName"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("productName")
                  ? "field-error-color"
                  : ""
                  } `}
                value={vendorAssessmentForm.productName}
                onChange={handleInputChange("productName")}
                disabled={
                  !vendorAssessmentFormPermissions.productName?.isEditable ||
                  isFieldDisabled("productName") ||
                  disableProductName
                }
                onFocus={() => handleFieldFocus("productName")}
                onBlur={() => handleBlur("productName")}
                maxLength={Constants.PRODUCT_NAME_MAX_LENGTH}
              />
            </Box>

            {/* Type of Vendor */}
            <Box hidden={!showVaField("typeOfVendor")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("typeOfVendor")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.TYPE_OF_VENDOR}
                htmlFor="typeOfVendor"
                required={
                  vendorAssessmentFormPermissions.typeOfVendor.isMandatory
                }
              />
              <Dropdown
                id="typeOfVendor"
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("typeOfVendor")
                  ? "field-error-color"
                  : ""
                  } `}
                options={formatRawDropdownOptions(
                  dropdownData.vendorTypes,
                  "name",
                  "id"
                )}
                name="typeOfVendor"
                selectedValue={vendorAssessmentForm.typeOfVendor}
                onChange={handleDropdownChange("typeOfVendor")}
                disabled={!vendorAssessmentFormPermissions.typeOfVendor?.isEditable || isFieldDisabled("typeOfVendor")}
                onFocus={() => handleFieldFocus("typeOfVendor")}
                onBlur={() => handleBlur("typeOfVendor")}
                    isClearable
                  />
            </Box>

            {/* Added On */}
            <Box hidden={!showVaField("addedOn")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("addedOn")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.ADDED_ON}
                htmlFor="addedOn"
                required={
                  vendorAssessmentFormPermissions.addedOn.isMandatory
                }
              />
              <CustomDatePicker
                id="addedOn"
                selected={vendorAssessmentForm.addedOn}
                placeholder={placeholderTexts?.DATE}
                onChange={handleDateChange("addedOn")}
                dateFormat="MM/dd/yyyy"
                minDate={new Date()}
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("addedOn")
                  ? "field-error-color"
                  : ""
                  } `}
                disabled={!vendorAssessmentFormPermissions.addedOn?.isEditable || isFieldDisabled("addedOn")}
                onFocus={() => handleFieldFocus("addedOn")}
                onBlur={() => handleBlur("addedOn")}
              />
            </Box>

            {/* Currently Manufacture Same / Similar Product */}
            <Box hidden={!showVaField("currentlyManufactureSameSimilarProduct")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("currentlyManufactureSameSimilarProduct")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.CURRENTLY_MANUFACTURE_SAME_SIMILAR_PRODUCT}
                htmlFor="currentlyManufactureSameSimilarProduct"
                required={
                  vendorAssessmentFormPermissions.currentlyManufactureSameSimilarProduct.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_CURRENTLY_MANUFACTURE_SAME_OR_SIMILAR_PRODUCT_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                id="currentlyManufactureSameSimilarProduct"
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("currentlyManufactureSameSimilarProduct")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="currentlyManufactureSameSimilarProduct"
                selectedValue={
                  vendorAssessmentForm.currentlyManufactureSameSimilarProduct
                }
                onChange={handleDropdownChange(
                  "currentlyManufactureSameSimilarProduct"
                )}
                disabled={!vendorAssessmentFormPermissions.currentlyManufactureSameSimilarProduct?.isEditable || isFieldDisabled("currentlyManufactureSameSimilarProduct")}
                onFocus={() => handleFieldFocus("currentlyManufactureSameSimilarProduct")}
                onBlur={() => handleBlur("currentlyManufactureSameSimilarProduct")}
                    isClearable
                  />
            </Box>

            {manufactureSameSimilarAnswer === "yes" && (
              <Box hidden={!showVaField("mostSimilarProduct")}>
                <Label
                  className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("mostSimilarProduct")
                    ? "label-error-text"
                    : ""
                    } `}
                  text={vendorAssessmentLabels?.MOST_SIMILAR_PRODUCT}
                  htmlFor="mostSimilarProduct"
                  required={
                    vendorAssessmentFormPermissions.mostSimilarProduct.isMandatory
                  }
                />
                <Input
                  id="mostSimilarProduct"
                  name="mostSimilarProduct"
                  className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("mostSimilarProduct")
                    ? "field-error-color"
                    : ""
                    } `}
                  value={vendorAssessmentForm.mostSimilarProduct}
                  onChange={handleInputChange("mostSimilarProduct")}
                  disabled={!vendorAssessmentFormPermissions.mostSimilarProduct?.isEditable || isFieldDisabled("mostSimilarProduct")}
                  onFocus={() => handleFieldFocus("mostSimilarProduct")}
                  onBlur={() => handleBlur("mostSimilarProduct")}
                  maxLength={Constants.VA_INPUT_MAX_LENGTH}
                />
              </Box>
            )}
            {manufactureSameSimilarAnswer === "no" && (
              <Box hidden={!showVaField("experienceWithSimilarType")}>
                <Label
                  className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("experienceWithSimilarType")
                    ? "label-error-text"
                    : ""
                    } `}
                  text={vendorAssessmentLabels?.EXPERIENCE_WITH_SIMILAR_TYPE}
                  htmlFor="experienceWithSimilarType"
                  required={
                    vendorAssessmentFormPermissions.experienceWithSimilarType.isMandatory
                  }
                />
                <Input
                  id="experienceWithSimilarType"
                  name="experienceWithSimilarType"
                  className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("experienceWithSimilarType")
                    ? "field-error-color"
                    : ""
                    } `}
                  value={vendorAssessmentForm.experienceWithSimilarType}
                  onChange={handleInputChange("experienceWithSimilarType")}
                  disabled={!vendorAssessmentFormPermissions.experienceWithSimilarType?.isEditable || isFieldDisabled("experienceWithSimilarType")}
                  onFocus={() => handleFieldFocus("experienceWithSimilarType")}
                  onBlur={() => handleBlur("experienceWithSimilarType")}
                  maxLength={Constants.VA_INPUT_MAX_LENGTH}
                />
              </Box>
            )}

            {/* Country of Origin */}
            <Box hidden={!showVaField("countryOfOrigin")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("countryOfOrigin")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.COUNTRY_OF_ORIGIN}
                htmlFor="countryOfOrigin"
                required={
                  vendorAssessmentFormPermissions.countryOfOrigin.isMandatory
                }
              />
              {isProduceRacLayout ? (
                <MultipleSelect
                  options={countryDropdownOptions}
                  value={countryOptionsSelected}
                  onChange={(value: MultiSelectOption[]) =>
                    handleCountryMultipleSelection(value)
                  }
                  disabled={
                    !vendorAssessmentFormPermissions?.countryOfOrigin
                      ?.isEditable || isFieldDisabled("countryOfOrigin")
                  }
                  className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("countryOfOrigin")
                    ? "field-error-color-multiple-select"
                    : ""
                    } `}
                  onFocus={() => handleFieldFocus("countryOfOrigin")}
                  onBlur={() => handleBlur("countryOfOrigin")}
                />
              ) : (
                <DropdownAutoSuggest
                  className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("countryOfOrigin")
                    ? "field-error-color"
                    : ""
                    } `}
                  options={formatRawDropdownOptions(
                    dropdownData.countries,
                    "name",
                    "id"
                  )}
                  value={(() => {
                    const countryId = vendorAssessmentForm?.countryOfOrigin;
                    if (
                      countryId === undefined ||
                      countryId === null ||
                      countryId === ""
                    ) {
                      return null;
                    }
                    const country = dropdownData.countries.find(
                      (c: any) =>
                        toNumberIfPossible(c.id) ===
                        toNumberIfPossible(countryId)
                    );
                    return country
                      ? {
                          label: country.name,
                          value: toNumberIfPossible(country.id),
                        }
                      : null;
                  })()}
                  placeholder="Select"
                  onChange={(option) => {
                    if (option == null || option.value === "" || option.value == null) {
                      handleDropdownChange("countryOfOrigin")(
                        {} as React.ChangeEvent<HTMLInputElement>,
                        null
                      );
                      return;
                    }
                    const formattedOption = {
                      value: option.value,
                      id: option.value,
                      label: option.label,
                    };
                    handleDropdownChange("countryOfOrigin")(
                      {} as React.ChangeEvent<HTMLInputElement>,
                      formattedOption
                    );
                  }}
                  onFocus={() => handleFieldFocus("countryOfOrigin")}
                  onBlur={() => handleBlur("countryOfOrigin")}
                  isDisabled={
                    !vendorAssessmentFormPermissions?.countryOfOrigin
                      ?.isEditable || isFieldDisabled("countryOfOrigin")
                  }
                    isClearable
                  />
              )}
            </Box>

            {isProduceRacLayout && showVaField("canTrackLotCodes") ? (
              <Box>
                <Label
                  className={`${darkMode ? "dark" : "light"} ${vendorAssessmentFormErrors.includes("canTrackLotCodes")
                    ? "label-error-text"
                    : ""
                    } `}
                  text={vendorAssessmentLabels?.CAN_TRACK_LOT_CODE}
                  htmlFor="canTrackLotCodes-rac"
                  required={
                    vendorAssessmentFormPermissions.canTrackLotCodes.isMandatory
                  }
                />
                <Dropdown
                  id="canTrackLotCodes-rac"
                  placeholder="Select"
                  className={`${darkMode ? "dark" : "light"} ${vendorAssessmentFormErrors.includes("canTrackLotCodes")
                    ? "field-error-color"
                    : ""
                    } `}
                  options={YES_NO_OPTIONS}
                  name="canTrackLotCodes"
                  selectedValue={vendorAssessmentForm.canTrackLotCodes}
                  onChange={handleDropdownChange("canTrackLotCodes")}
                  disabled={!vendorAssessmentFormPermissions.canTrackLotCodes?.isEditable || isFieldDisabled("canTrackLotCodes")}
                  onFocus={() => handleFieldFocus("canTrackLotCodes")}
                  onBlur={() => handleBlur("canTrackLotCodes")}
                    isClearable
                  />
              </Box>
            ) : null}

            {/* Prop 65 Warnings?  */}
            <Box hidden={!showVaField("prop65Warning")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("prop65Warning")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.PROP_65_WARNINGS}
                htmlFor="prop65Warning"
                required={
                  vendorAssessmentFormPermissions.prop65Warning.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_PROP_65_WARNING_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                id="prop65Warning"
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("prop65Warning")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="prop65Warning"
                selectedValue={vendorAssessmentForm.prop65Warning}
                onChange={handleDropdownChange("prop65Warning")}
                disabled={!vendorAssessmentFormPermissions.prop65Warning?.isEditable || isFieldDisabled("prop65Warning")}
                onFocus={() => handleFieldFocus("prop65Warning")}
                onBlur={() => handleBlur("prop65Warning")}
                    isClearable
                  />
            </Box>

            {/* Currently Private Label for US Companies? */}
            <Box hidden={!showVaField("privateLabelFosUsCompanies")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("privateLabelFosUsCompanies")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.CURRENTLY_PRIVATE_LABEL_FOR_US_COMPANIES}
                htmlFor="privateLabelFosUsCompanies"
                required={
                  vendorAssessmentFormPermissions.privateLabelFosUsCompanies.isMandatory
                }
              />
              <Dropdown
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("privateLabelFosUsCompanies")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="privateLabelFosUsCompanies"
                selectedValue={
                  vendorAssessmentForm.privateLabelFosUsCompanies
                }
                onChange={handleDropdownChange(
                  "privateLabelFosUsCompanies"
                )}
                disabled={!vendorAssessmentFormPermissions.privateLabelFosUsCompanies?.isEditable || isFieldDisabled("privateLabelFosUsCompanies")}
                onFocus={() => handleFieldFocus("privateLabelFosUsCompanies")}
                onBlur={() => handleBlur("privateLabelFosUsCompanies")}
                    isClearable
                  />
            </Box>

            {/* Capacity - Production Run */}
            <Box hidden={!showVaField("capacityProductionRun")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("capacityProductionRun")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.CAPACITY_PRODUCTION_RUN}
                htmlFor="capacityProductionRun"
                required={
                  vendorAssessmentFormPermissions.capacityProductionRun.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_CAPACITY_PRODUCTION_RUN_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Input
                id="capacityProductionRun"
                name="capacityProductionRun"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("capacityProductionRun")
                  ? "field-error-color"
                  : ""
                  } `}
                value={vendorAssessmentForm.capacityProductionRun}
                onChange={handleInputChange("capacityProductionRun")}
                disabled={!vendorAssessmentFormPermissions.capacityProductionRun?.isEditable || isFieldDisabled("capacityProductionRun")}
                onFocus={() => handleFieldFocus("capacityProductionRun")}
                onBlur={() => handleBlur("capacityProductionRun")}
                maxLength={Constants.VA_INPUT_MAX_LENGTH}
                enableNumberFormatting={true}
              />
            </Box>

            {/* Where are the manufacturing facilities located? */}
             <Box hidden={!showVaField("manufacturingFacilityLocations")}>
              <Label
                className={`${darkMode ? "dark" : "light"} ${vendorAssessmentFormErrors.includes("manufacturingFacilityLocations")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.MANUFACTURING_FACILITY_LOCATIONS}
                htmlFor="manufacturingFacilityLocations"
                required={
                  vendorAssessmentFormPermissions.manufacturingFacilityLocations.isMandatory
                }
              />
              <Input
                id="manufacturingFacilityLocations"
                name="manufacturingFacilityLocations"
                className={`${darkMode ? "dark" : "light"} ${vendorAssessmentFormErrors.includes("manufacturingFacilityLocations")
                  ? "field-error-color"
                  : ""
                  } `}
                value={vendorAssessmentForm.manufacturingFacilityLocations}
                onChange={handleInputChange("manufacturingFacilityLocations")}
                disabled={!vendorAssessmentFormPermissions.manufacturingFacilityLocations?.isEditable || isFieldDisabled("manufacturingFacilityLocations")}
                onFocus={() => handleFieldFocus("manufacturingFacilityLocations")}
                onBlur={() => handleBlur("manufacturingFacilityLocations")}
                maxLength={Constants.VA_INPUT_MAX_LENGTH}
              />
            </Box>

            {/* Capacity - Production Lines */}
            <Box hidden={!showVaField("capacityProductionLines")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("capacityProductionLines")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.CAPACITY_PRODUCTION_LINES}
                htmlFor="capacityProductionLines"
                required={
                  vendorAssessmentFormPermissions.capacityProductionLines.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_CAPACITY_PRODUCTION_LINES_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Input
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("capacityProductionLines")
                  ? "field-error-color"
                  : ""
                  } `}
                value={vendorAssessmentForm.capacityProductionLines}
                onChange={handleInputChange("capacityProductionLines")}
                disabled={!vendorAssessmentFormPermissions.capacityProductionLines?.isEditable || isFieldDisabled("capacityProductionLines")}
                onFocus={() => handleFieldFocus("capacityProductionLines")}
                onBlur={() => handleBlur("capacityProductionLines")}
                maxLength={Constants.VA_INPUT_MAX_LENGTH}
              />
            </Box>

            {/* How Many Facilities */}
            <Box hidden={!showVaField("capacityFacilities")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("capacityFacilities")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.HOW_MANY_FACILITIES}
                htmlFor="capacityFacilities"
                required={
                  vendorAssessmentFormPermissions.capacityFacilities.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_HOW_MANY_FACILITIES_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Input
                id="capacityFacilities"
                name="capacityFacilities"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("capacityFacilities")
                  ? "field-error-color"
                  : ""
                  } `}
                value={vendorAssessmentForm.capacityFacilities}
                onChange={handleInputChange("capacityFacilities")}
                disabled={!vendorAssessmentFormPermissions.capacityFacilities?.isEditable || isFieldDisabled("capacityFacilities")}
                onFocus={() => handleFieldFocus("capacityFacilities")}
                onBlur={() => handleBlur("capacityFacilities")}
                maxLength={Constants.VA_INPUT_MAX_LENGTH}
              />
            </Box>

            {/* Outsource Production? */}
            <Box hidden={!showVaField("outsourceProduction")}>
              <Label
                className={`${darkMode ? "dark" : "light"} label-icon ${vendorAssessmentFormErrors.includes("outsourceProduction")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.OUTSOURCE_PRODUCTION}
                htmlFor="outsourceProduction"
                required={
                  vendorAssessmentFormPermissions.outsourceProduction.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_OUTSOURCE_PRODUCTION_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                id="outsourceProduction"
                placeholder="Select"
                className={`${darkMode ? "dark" : "light"} ${vendorAssessmentFormErrors.includes("outsourceProduction")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="outsourceProduction"
                selectedValue={vendorAssessmentForm.outsourceProduction}
                onChange={handleDropdownChange("outsourceProduction")}
                disabled={!vendorAssessmentFormPermissions.outsourceProduction?.isEditable || isFieldDisabled("outsourceProduction")}
                onFocus={() => handleFieldFocus("outsourceProduction")}
                onBlur={() => handleBlur("outsourceProduction")}
                    isClearable
                  />
            </Box>

            {/* Same Type of Product Under Consideration? */}
            <Box hidden={!showVaField("sameTypeOfProductUnderConsideration")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("sameTypeOfProductUnderConsideration")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.SAME_TYPE_OF_PRODUCT_UNDER_CONSIDERATION}
                htmlFor="sameTypeOfProductUnderConsideration"
                required={
                  vendorAssessmentFormPermissions.sameTypeOfProductUnderConsideration.isMandatory
                }
              >
               <CustomTooltip tooltipText={infoIconMessages?.SAME_TYPE_OF_PRODUCT_UNDER_CONSIDERATION_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("sameTypeOfProductUnderConsideration")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="sameTypeOfProductUnderConsideration"
                selectedValue={
                  vendorAssessmentForm.sameTypeOfProductUnderConsideration
                }
                onChange={handleDropdownChange(
                  "sameTypeOfProductUnderConsideration"
                )}
                disabled={!vendorAssessmentFormPermissions.sameTypeOfProductUnderConsideration?.isEditable || isFieldDisabled("sameTypeOfProductUnderConsideration")}
                onFocus={() => handleFieldFocus("sameTypeOfProductUnderConsideration")}
                onBlur={() => handleBlur("sameTypeOfProductUnderConsideration")}
                    isClearable
                  />
            </Box>

            {/* Manufacturing Facility GFSI Certified? */}
            <Box hidden={!showVaField("facilityGfsiCertified")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("facilityGfsiCertified")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.MANUFACTURING_FACILITY_GFSI_CERTIFIED}
                htmlFor="facilityGfsiCertified"
                required={
                  vendorAssessmentFormPermissions.facilityGfsiCertified.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_MANUFACURING_FACILITY_GFSI_CERTIFIED_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                id="facilityGfsiCertified"
                placeholder="Select"
                className={`${darkModeClass} ${!isVendor && vendorAssessmentForm?.facilityGfsiCertified?.toLowerCase() === "no" ? "highlight"
                  : ""} ${vendorAssessmentFormErrors.includes("facilityGfsiCertified")
                    ? "field-error-color"
                    : ""
                  }`}
                options={YES_NO_OPTIONS}
                name="facilityGfsiCertified"
                selectedValue={
                  vendorAssessmentForm.facilityGfsiCertified
                }
                onChange={handleDropdownChange(
                  "facilityGfsiCertified"
                )}
                disabled={!vendorAssessmentFormPermissions.facilityGfsiCertified?.isEditable || isFieldDisabled("facilityGfsiCertified")}
                onFocus={() => handleFieldFocus("facilityGfsiCertified")}
                onBlur={() => handleBlur("facilityGfsiCertified")}
                    isClearable
                  />
            </Box>

            {/* Certification Withdrawn Within Last 5 Years? */}
            <Box hidden={!showVaField("certificationWithdrawnWithinLast5Years")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("certificationWithdrawnWithinLast5Years")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.CERTIFICATION_WITHDRAWN_WITHIN_LAST_5_YEARS}
                htmlFor="certificationWithdrawnWithinLast5Years"
                required={
                  vendorAssessmentFormPermissions.certificationWithdrawnWithinLast5Years.isMandatory
                }
              />
              <Dropdown
                placeholder="Select"
                className={`${darkModeClass} ${!isVendor && vendorAssessmentForm?.certificationWithdrawnWithinLast5Years?.toLowerCase() === "yes" ? "highlight" : ""} ${vendorAssessmentFormErrors.includes("certificationWithdrawnWithinLast5Years")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="certificationWithdrawnWithinLast5Years"
                selectedValue={vendorAssessmentForm.certificationWithdrawnWithinLast5Years}
                onChange={handleDropdownChange("certificationWithdrawnWithinLast5Years")}
                disabled={!vendorAssessmentFormPermissions.certificationWithdrawnWithinLast5Years?.isEditable || isFieldDisabled("certificationWithdrawnWithinLast5Years")}
                onFocus={() => handleFieldFocus("certificationWithdrawnWithinLast5Years")}
                onBlur={() => handleBlur("certificationWithdrawnWithinLast5Years")}
                    isClearable
                  />
            </Box>

            {/* HACCP/FSP Plan? */}
            <Box hidden={!showVaField("haccpFspPlan")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("haccpFspPlan")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.HACCP_FSP_PLAN}
                htmlFor="haccpFspPlan"
                required={
                  vendorAssessmentFormPermissions.haccpFspPlan.isMandatory
                }
              />
              <Dropdown
                id="haccpFspPlan"
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("haccpFspPlan")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="haccpFspPlan"
                selectedValue={vendorAssessmentForm.haccpFspPlan}
                onChange={handleDropdownChange("haccpFspPlan")}
                disabled={!vendorAssessmentFormPermissions.haccpFspPlan?.isEditable || isFieldDisabled("certificationWithdrawnWithinLast5Years")}
                onFocus={() => handleFieldFocus("haccpFspPlan")}
                onBlur={() => handleBlur("haccpFspPlan")}
                    isClearable
                  />
            </Box>

            {/* Test finished products for pathogens */}
            <Box hidden={!showVaField("testFinishedProductsForPathogens")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("testFinishedProductsForPathogens")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.TEST_FINISHED_PRODUCTS_FOR_PATHOGENS}
                htmlFor="testFinishedProductsForPathogens"
                required={
                  vendorAssessmentFormPermissions.testFinishedProductsForPathogens.isMandatory
                }
              />
              <Dropdown
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("testFinishedProductsForPathogens")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="testFinishedProductsForPathogens"
                selectedValue={
                  vendorAssessmentForm.testFinishedProductsForPathogens
                }
                onChange={handleDropdownChange(
                  "testFinishedProductsForPathogens"
                )}
                disabled={!vendorAssessmentFormPermissions.testFinishedProductsForPathogens?.isEditable || isFieldDisabled("testFinishedProductsForPathogens")}
                onFocus={() => handleFieldFocus("testFinishedProductsForPathogens")}
                onBlur={() => handleBlur("testFinishedProductsForPathogens")}
                    isClearable
                  />
            </Box>

            {/* Metal Detection/Xray On Product Line */}
            <Box hidden={!showVaField("metalDetectionXrayOnProductLine")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("metalDetectionXrayOnProductLine")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.METAL_DETECTION_XRAY_ON_PRODUCT_LINE}
                htmlFor="metalDetectionXrayOnProductLine"
                required={
                  vendorAssessmentFormPermissions.metalDetectionXrayOnProductLine.isMandatory
                }
              />
              <Dropdown
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("metalDetectionXrayOnProductLine")
                  ? "field-error-color"
                  : ""
                  } `}
                options={metalDetectionXrayOptions}
                name="metalDetectionXrayOnProductLine"
                selectedValue={
                  vendorAssessmentForm.metalDetectionXrayOnProductLine
                }
                onChange={handleDropdownChange(
                  "metalDetectionXrayOnProductLine"
                )}
                disabled={!vendorAssessmentFormPermissions.metalDetectionXrayOnProductLine?.isEditable || isFieldDisabled("metalDetectionXrayOnProductLine")}
                onFocus={() => handleFieldFocus("metalDetectionXrayOnProductLine")}
                onBlur={() => handleBlur("metalDetectionXrayOnProductLine")}
                    isClearable
                  />
            </Box>

            {showOtherMetalDetectionXrayField && (
              <Box>
                <Label
                  className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("otherMetalDetectionXrayOnProductLine")
                    ? "label-error-text"
                    : ""
                    } `}
                  text={vendorAssessmentLabels?.OTHER_METAL_DETECTION_XRAY_ON_PRODUCT_LINE}
                  htmlFor="otherMetalDetectionXrayOnProductLine"
                  required
                />
                <Input
                  id="otherMetalDetectionXrayOnProductLine"
                  name="otherMetalDetectionXrayOnProductLine"
                  className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("otherMetalDetectionXrayOnProductLine")
                    ? "field-error-color"
                    : ""
                    } `}
                  value={vendorAssessmentForm.otherMetalDetectionXrayOnProductLine}
                  onChange={handleInputChange("otherMetalDetectionXrayOnProductLine")}
                  disabled={
                    !vendorAssessmentFormPermissions.metalDetectionXrayOnProductLine?.isEditable ||
                    isFieldDisabled("metalDetectionXrayOnProductLine")
                  }
                  onFocus={() => handleFieldFocus("otherMetalDetectionXrayOnProductLine")}
                  onBlur={() => handleBlur("otherMetalDetectionXrayOnProductLine")}
                  maxLength={Constants.VA_INPUT_MAX_LENGTH}
                />
              </Box>
            )}

            {/* Allergen Management Program */}
            <Box hidden={!showVaField("allergenManagementProgram")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("allergenManagementProgram")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.ALLERGEN_MANAGEMENT_PROGRAM}
                htmlFor="allergenManagementProgram"
                required={
                  vendorAssessmentFormPermissions.allergenManagementProgram.isMandatory
                }
              />
              {/* <Input
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("allergenManagementProgram")
                  ? "field-error-color"
                  : ""
                  } `}
                value={vendorAssessmentForm.allergenManagementProgram}
                onChange={handleInputChange("allergenManagementProgram")}
                disabled={!vendorAssessmentFormPermissions.allergenManagementProgram?.isEditable}
                onFocus={() => handleFieldFocus("allergenManagementProgram")}
                onBlur={() => handleBlur("allergenManagementProgram")}
                maxLength={Constants.VA_INPUT_MAX_LENGTH}
              /> */}
              <Dropdown
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("allergenManagementProgram")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="allergenManagementProgram"
                selectedValue={
                  vendorAssessmentForm.allergenManagementProgram
                }
                onChange={handleDropdownChange(
                  "allergenManagementProgram"
                )}
                disabled={!vendorAssessmentFormPermissions.allergenManagementProgram?.isEditable || isFieldDisabled("allergenManagementProgram")}
                onFocus={() => handleFieldFocus("allergenManagementProgram")}
                onBlur={() => handleBlur("allergenManagementProgram")}
                    isClearable
                  />
            </Box>

            {/* Product Recall Procedure */}
            <Box hidden={!showVaField("productRecallProcedure")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("productRecallProcedure")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.PRODUCT_RECALL_PROCEDURE}
                htmlFor="productRecallProcedure"
                required={
                  vendorAssessmentFormPermissions.productRecallProcedure.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_PRODUCT_RECALL_PROCEDURE_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                id="productRecallProcedure"
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("productRecallProcedure")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="productRecallProcedure"
                selectedValue={
                  vendorAssessmentForm.productRecallProcedure
                }
                onChange={handleDropdownChange(
                  "productRecallProcedure"
                )}
                disabled={!vendorAssessmentFormPermissions.productRecallProcedure?.isEditable || isFieldDisabled("productRecallProcedure")}
                onFocus={() => handleFieldFocus("productRecallProcedure")}
                onBlur={() => handleBlur("productRecallProcedure")}
                    isClearable
                  />
            </Box>

            {/* Recall In Last 5 Years? */}
            <Box hidden={!showVaField("recallWithinLast5Years")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("recallWithinLast5Years")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.RECALL_WITHIN_LAST_5_YEARS}
                htmlFor="recallWithinLast5Years"
                required={
                  vendorAssessmentFormPermissions.recallWithinLast5Years.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_RECALL_IN_LAST_5_YEARS_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                placeholder="Select"
                className={`${darkModeClass} ${!isVendor && vendorAssessmentForm?.recallWithinLast5Years?.toLowerCase() === "yes" ? "highlight"
                  : ""} ${vendorAssessmentFormErrors.includes("recallWithinLast5Years")
                    ? "field-error-color"
                    : ""
                  }`}
                options={YES_NO_OPTIONS}
                name="recallWithinLast5Years"
                selectedValue={vendorAssessmentForm.recallWithinLast5Years}
                onChange={handleDropdownChange("recallWithinLast5Years")}
                disabled={!vendorAssessmentFormPermissions.recallWithinLast5Years?.isEditable || isFieldDisabled("recallWithinLast5Years")}
                onFocus={() => handleFieldFocus("recallWithinLast5Years")}
                onBlur={() => handleBlur("recallWithinLast5Years")}
                    isClearable
                  />
            </Box>

            {/* If Recall, Please Detail */}
            <Box hidden={!showVaField("recallDetail")}>
              <Label
                className={`${darkModeClass} ${!(!vendorAssessmentFormPermissions.recallDetail?.isEditable || 
                  vendorAssessmentForm.recallWithinLast5Years?.toLowerCase() === "no" || 
                  isFieldDisabled("recallDetail")) && 
                  vendorAssessmentFormErrors.includes("recallDetail")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.IF_RECALL_PLEASE_DETAIL}
                htmlFor="recallDetail"
                required={
                  vendorAssessmentFormPermissions.recallDetail.isMandatory
                }
              />
              <Input
                id="recallDetail"
                name="recallDetail"
                className={`${darkModeClass} ${
                  !(!vendorAssessmentFormPermissions.recallDetail?.isEditable || 
                    vendorAssessmentForm.recallWithinLast5Years?.toLowerCase() === "no" || 
                    isFieldDisabled("recallDetail")) && 
                    vendorAssessmentFormErrors.includes("recallDetail")
                  ? "field-error-color"
                  : ""
                  } `}
                value={vendorAssessmentForm.recallDetail}
                onChange={handleInputChange("recallDetail")}
                disabled={!vendorAssessmentFormPermissions.recallDetail?.isEditable || vendorAssessmentForm.recallWithinLast5Years?.toLowerCase() === "no" || isFieldDisabled("recallDetail")}
                onFocus={() => handleFieldFocus("recallDetail")}
                onBlur={() => handleBlur("recallDetail")}
                maxLength={Constants.VA_INPUT_MAX_LENGTH}
              />
            </Box>

            {/* Facility Registered with FDA or USDA Inspected? */}
            <Box hidden={!showVaField("facilityFdaUsdaInspected")}>
              <Label
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("facilityFdaUsdaInspected")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.FACILITY_FDA_USDA_INSPECTED}
                htmlFor="facilityFdaUsdaInspected"
                required={
                  vendorAssessmentFormPermissions.facilityFdaUsdaInspected.isMandatory
                }
              />
              <Dropdown
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("facilityFdaUsdaInspected")
                  ? "field-error-color"
                  : ""
                  } `}
                options={formatRawDropdownOptions(
                  dropdownData.fdaUsda,
                  "name",
                  "id"
                )}
                name="facilityFdaUsdaInspected"
                selectedValue={vendorAssessmentForm.facilityFdaUsdaInspected}
                onChange={handleDropdownChange(
                  "facilityFdaUsdaInspected"
                )}
                disabled={!vendorAssessmentFormPermissions.facilityFdaUsdaInspected?.isEditable || isFieldDisabled("facilityFdaUsdaInspected")}
                onFocus={() => handleFieldFocus("facilityFdaUsdaInspected")}
                onBlur={() => handleBlur("facilityFdaUsdaInspected")}
                    isClearable
                  />
            </Box>

            {/* If USDA, Plant Number */}
            <Box hidden={!showVaField("usdaPlantNo")}>
              <Label
                className={`${darkModeClass} label-icon ${!(!vendorAssessmentFormPermissions.usdaPlantNo?.isEditable || 
                    vendorAssessmentForm.usdaPlantNo === "Not Applicable" || 
                    isFieldDisabled("usdaPlantNo")) && 
                    vendorAssessmentFormErrors.includes("usdaPlantNo")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.IF_USDA_PLANT_NUMBER}
                htmlFor="usdaPlantNo"
                required={
                  vendorAssessmentFormPermissions.usdaPlantNo.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_IF_USDA_PLANT_NUMBER_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Input
                className={`${darkModeClass} ${
                  !(!vendorAssessmentFormPermissions.usdaPlantNo?.isEditable || 
                    vendorAssessmentForm.usdaPlantNo === "Not Applicable" || 
                    isFieldDisabled("usdaPlantNo")) && 
                    vendorAssessmentFormErrors.includes("usdaPlantNo")
                  ? "field-error-color"
                  : ""
                  } `}
                value={vendorAssessmentForm.usdaPlantNo}
                onChange={handleInputChange("usdaPlantNo")}
                disabled={!vendorAssessmentFormPermissions.usdaPlantNo?.isEditable || vendorAssessmentForm.usdaPlantNo === "Not Applicable" || isFieldDisabled("usdaPlantNo")}
                onFocus={() => handleFieldFocus("usdaPlantNo")}
                onBlur={() => handleBlur("usdaPlantNo")}
                maxLength={Constants.VA_INPUT_MAX_LENGTH}
              />
            </Box>

            {/* Regulatory Enforcement Action? */}
            <Box hidden={!showVaField("regulatoryEnforcementAction")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("regulatoryEnforcementAction")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.REGULATORY_ENFORCEMENT_ACTION}
                htmlFor="regulatoryEnforcementAction"
                required={
                  vendorAssessmentFormPermissions.regulatoryEnforcementAction.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_REGULATORY_ENFORCEMENT_ACTION_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                id="regulatoryEnforcementAction"
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("regulatoryEnforcementAction")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="regulatoryEnforcementAction"
                selectedValue={vendorAssessmentForm.regulatoryEnforcementAction}
                onChange={handleDropdownChange("regulatoryEnforcementAction")}
                disabled={!vendorAssessmentFormPermissions.regulatoryEnforcementAction?.isEditable || isFieldDisabled("regulatoryEnforcementAction")}
                onFocus={() => handleFieldFocus("regulatoryEnforcementAction")}
                onBlur={() => handleBlur("regulatoryEnforcementAction")}
                    isClearable
                  />
            </Box>

            {/* Adulterated Product Policies/Procedures */}
            <Box hidden={!showVaField("adulteratedProductPoliciesProcedures")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("adulteratedProductPoliciesProcedures")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.ADULTERATED_PRODUCT_POLICIES_PROCEDURES}
                htmlFor="adulteratedProductPoliciesProcedures"
                required={
                  vendorAssessmentFormPermissions.adulteratedProductPoliciesProcedures.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_ADULTERATED_PRODUCT_POLICIES_OR_PROCEDURES_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("adulteratedProductPoliciesProcedures")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="adulteratedProductPoliciesProcedures"
                selectedValue={
                  vendorAssessmentForm.adulteratedProductPoliciesProcedures
                }
                onChange={handleDropdownChange(
                  "adulteratedProductPoliciesProcedures"
                )}
                disabled={!vendorAssessmentFormPermissions.adulteratedProductPoliciesProcedures?.isEditable || isFieldDisabled("adulteratedProductPoliciesProcedures")}
                onFocus={() => handleFieldFocus("adulteratedProductPoliciesProcedures")}
                onBlur={() => handleBlur("adulteratedProductPoliciesProcedures")}
                    isClearable
                  />
            </Box>

            {/* If Importing, Automatic Detention? */}
            <Box hidden={!showVaField("importAutomaticDetention")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("importAutomaticDetention")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.IF_IMPORTING_AUTOMATIC_DETENTION}
                htmlFor="importAutomaticDetention"
                required={
                  vendorAssessmentFormPermissions.importAutomaticDetention.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_IF_IMPORTING_AUTOMATIC_DETENTION_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                id="importAutomaticDetention"
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("importAutomaticDetention")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="importAutomaticDetention"
                selectedValue={vendorAssessmentForm.importAutomaticDetention}
                onChange={handleDropdownChange("importAutomaticDetention")}
                disabled={!vendorAssessmentFormPermissions.importAutomaticDetention?.isEditable || isFieldDisabled("importAutomaticDetention")}
                onFocus={() => handleFieldFocus("importAutomaticDetention")}
                onBlur={() => handleBlur("importAutomaticDetention")}
                    isClearable
                  />
            </Box>

             {/* If Outsource, Describe */}
            <Box hidden={!showVaField("outsourceDescripton")}>
              <Label
                className={`${darkMode ? "dark" : "light"} ${!(!vendorAssessmentFormPermissions.outsourceDescripton?.isEditable || 
                    vendorAssessmentForm.outsourceProduction?.toLowerCase() === "no" || 
                    isFieldDisabled("outsourceDescripton")) && 
                    vendorAssessmentFormErrors.includes("outsourceDescripton")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.OUTSOURCE_DESCRIPTION}
                htmlFor="outsourceDescripton"
                required={
                  vendorAssessmentFormPermissions.outsourceDescripton.isMandatory
                }
              />
              <Input
                id="outsourceDescripton"
                name="outsourceDescripton"
                className={`${darkMode ? "dark" : "light"} ${!(!vendorAssessmentFormPermissions.outsourceDescripton?.isEditable || 
                    vendorAssessmentForm.outsourceProduction?.toLowerCase() === "no" || 
                    isFieldDisabled("outsourceDescripton")) && 
                    vendorAssessmentFormErrors.includes("outsourceDescripton")
                  ? "field-error-color"
                  : ""
                  } `}
                value={vendorAssessmentForm.outsourceDescripton}
                onChange={handleInputChange("outsourceDescripton")}
                disabled={!vendorAssessmentFormPermissions.outsourceDescripton?.isEditable || vendorAssessmentForm.outsourceProduction?.toLowerCase() === 'no' || isFieldDisabled("outsourceDescripton")}
                onFocus={() => handleFieldFocus("outsourceDescripton")}
                onBlur={() => handleBlur("outsourceDescripton")}
                maxLength={Constants.VA_INPUT_MAX_LENGTH}
              />
            </Box>

            {/* Factor */}
            <Box hidden={!showVaField("factor")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("factor")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.FACTOR}
                htmlFor="Factor"
                required={
                  vendorAssessmentFormPermissions.factor.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_FACTOR_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                id="factor"
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("factor")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="factor"
                selectedValue={
                  vendorAssessmentForm.factor
                }
                onChange={handleDropdownChange(
                  "factor"
                )}
                disabled={!vendorAssessmentFormPermissions.factor?.isEditable || isFieldDisabled("factor")}
                onFocus={() => handleFieldFocus("factor")}
                onBlur={() => handleBlur("factor")}
                    isClearable
                  />
            </Box>

            {/* Labor Compliance Plan */}
            <Box hidden={!showVaField("childLaborVendor")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("childLaborVendor")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.CHILD_LABOR_VENDOR}
                htmlFor="childLaborVendor"
                required={
                  vendorAssessmentFormPermissions.childLaborVendor.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_CHILD_LABOUR_VENDOR_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                id="childLaborVendor"
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("childLaborVendor")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="childLaborVendor"
                selectedValue={vendorAssessmentForm.childLaborVendor}
                onChange={handleDropdownChange("childLaborVendor")}
                disabled={!vendorAssessmentFormPermissions.childLaborVendor?.isEditable || isFieldDisabled("childLaborVendor")}
                onFocus={() => handleFieldFocus("childLaborVendor")}
                onBlur={() => handleBlur("childLaborVendor")}
                    isClearable
                  />
            </Box>

            {/* Animal Welfare/Fraud */}
            <Box hidden={!showVaField("animalWelfareFraud")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("animalWelfareFraud")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.ANIMAL_WELFARE_FRAUD}
                htmlFor="Animal Welfare/Fraud"
                required={
                  vendorAssessmentFormPermissions.animalWelfareFraud.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_ANIMAL_WELFARE_OR_FRAUD_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                id="animalWelfareFraud"
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("animalWelfareFraud")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="animalWelfareFraud"
                selectedValue={vendorAssessmentForm.animalWelfareFraud}
                onChange={handleDropdownChange("animalWelfareFraud")}
                disabled={!vendorAssessmentFormPermissions.animalWelfareFraud?.isEditable || isFieldDisabled("animalWelfareFraud")}
                onFocus={() => handleFieldFocus("animalWelfareFraud")}
                onBlur={() => handleBlur("animalWelfareFraud")}
                    isClearable
                  />
            </Box>

            {/* FSMA */}
            <Box hidden={!showVaField("fsma")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("fsma")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.FSMA_COMPLIANT}
                htmlFor="fsma"
                required={
                  vendorAssessmentFormPermissions.fsma.isMandatory
                }
              >
                <CustomTooltip tooltipText={infoIconMessages?.CP_VA_VAF_IS_ON_FDA_FTL}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <Dropdown
                placeholder="Select"
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("fsma")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="fsma"
                selectedValue={vendorAssessmentForm.fsma}
                onChange={handleDropdownChange("fsma")}
                disabled={!vendorAssessmentFormPermissions.fsma?.isEditable || isFieldDisabled("fsma")}
                onFocus={() => handleFieldFocus("fsma")}
                onBlur={() => handleBlur("fsma")}
                    isClearable
                  />
            </Box>

            {/* EDI */}
            <Box hidden={!showVaField("edi")}>
              <Label
                className={`${darkModeClass} label-icon ${vendorAssessmentFormErrors.includes("edi")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.EDI}
                htmlFor="edi"
                required={
                  vendorAssessmentFormPermissions.edi.isMandatory
                }
              >
              <CustomTooltip tooltipText={infoIconMessages?.EDI_INFO}>
                  <Image src={helpIcon} alt="Help Icon" />
                </CustomTooltip>
              </Label>
              <MultipleSelect
                options={formatRawDropdownOptions(
                  dropdownData.edi,
                  "name",
                  "id"
                )}
                value={optionsSelected}
                onChange={(value: any) => handleMultipleSelection(value)}
                disabled={!vendorAssessmentFormPermissions.edi?.isEditable || isFieldDisabled("edi")}
                className={`${darkModeClass} ${vendorAssessmentFormErrors.includes("edi")
                  ? "field-error-color-multiple-select"
                  : ""
                  } `}
                onFocus={() => handleFieldFocus("edi")}
                onBlur={() => handleBlur("edi")}
              />
            </Box>

            {/* Do you have the ability to track lot codes? */}
            <Box hidden={!showVaField("canTrackLotCodes") || isProduceRacLayout}>
              <Label
                className={`${darkMode ? "dark" : "light"} ${vendorAssessmentFormErrors.includes("canTrackLotCodes")
                  ? "label-error-text"
                  : ""
                  } `}
                text={vendorAssessmentLabels?.CAN_TRACK_LOT_CODE}
                htmlFor="canTrackLotCodes"
                required={
                  vendorAssessmentFormPermissions.canTrackLotCodes.isMandatory
                }
              />
              <Dropdown
                id="canTrackLotCodes"
                placeholder="Select"
                className={`${darkMode ? "dark" : "light"} ${vendorAssessmentFormErrors.includes("canTrackLotCodes")
                  ? "field-error-color"
                  : ""
                  } `}
                options={YES_NO_OPTIONS}
                name="canTrackLotCodes"
                selectedValue={vendorAssessmentForm.canTrackLotCodes}
                onChange={handleDropdownChange("canTrackLotCodes")}
                disabled={!vendorAssessmentFormPermissions.canTrackLotCodes?.isEditable || isFieldDisabled("canTrackLotCodes")}
                onFocus={() => handleFieldFocus("canTrackLotCodes")}
                onBlur={() => handleBlur("canTrackLotCodes")}
                    isClearable
                  />
            </Box>

          </Box>
        )}
      </form>
    </Box>
  );
};

export default VendorAssessmentForm;